import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface PerformanceRow {
  readonly performanceMetricId: string;
  readonly metricCode: string;
  readonly metricType: string;
  readonly subjectType: string | null;
  readonly subjectRef: string | null;
  readonly metricValue: string | null;
  readonly metricUnit: string | null;
  readonly benchmarkValue: string | null;
  readonly variance: string | null;
  readonly computedAt: string;
}

const METRIC_TYPES = ['PROCESS_EFFICIENCY', 'CYCLE_TIME', 'RESOURCE_UTILIZATION', 'SYSTEM_PERFORMANCE', 'CAMPAIGN_EFFECTIVENESS', 'APPROVAL_TIME', 'EXECUTION_TIME', 'THROUGHPUT'];

/**
 * M37 — Performance Dashboard (§28 / §15 Performance Management). Register of
 * recorded performance-metric snapshots (T500) across the 8 metric families
 * (process efficiency, cycle time, resource utilisation, throughput …), each
 * tied to a subject (process, campaign, system …), carrying the measured value,
 * its benchmark and the computed variance. Authors record a snapshot
 * (PERFORMANCE_MANAGE). Filterable by metric type, subject and period. Tenant
 * isolation is enforced server-side (LAW 4). A 403 surfaces a clear permission
 * message.
 */
export function PerformanceDashboard(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<PerformanceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [metricType, setMetricType] = useState('');
  const [subjectType, setSubjectType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // Record form
  const [newType, setNewType] = useState('PROCESS_EFFICIENCY');
  const [newSubjectType, setNewSubjectType] = useState('');
  const [subjectRef, setSubjectRef] = useState('');
  const [metricValue, setMetricValue] = useState('');
  const [metricUnit, setMetricUnit] = useState('');
  const [benchmarkValue, setBenchmarkValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricType) params.set('metricType', metricType);
    if (subjectType) params.set('subjectType', subjectType);
    if (fromDate) params.set('fromDate', new Date(fromDate).toISOString());
    if (toDate) params.set('toDate', new Date(toDate).toISOString());
    const qs = params.toString();
    apiRequest<{ data: PerformanceRow[]; total: number }>(`/v1/bi/performance${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricType, subjectType, fromDate, toDate, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: PerformanceRow }>(`/v1/bi/performance`, {
        method: 'POST',
        body: {
          metricType: newType,
          subjectType: newSubjectType.trim() || undefined,
          subjectRef: subjectRef.trim() || undefined,
          metricValue: metricValue.trim() ? Number(metricValue) : undefined,
          metricUnit: metricUnit.trim() || undefined,
          benchmarkValue: benchmarkValue.trim() ? Number(benchmarkValue) : undefined,
        },
        token,
      });
      setNotice(t('m37.performance.recorded', { code: r.data.metricCode, variance: r.data.variance ?? '—' }));
      setSubjectRef('');
      setMetricValue('');
      setBenchmarkValue('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.performance.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="performance-dashboard">
      <h1>{t('m37.performance.title')}</h1>

      <section aria-label="record-performance">
        <h2>{t('m37.performance.recordTitle')}</h2>
        <form aria-label="performance-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m37.performance.metricType')}
            <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {METRIC_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.performance.subjectType')}<input aria-label="field-subject-type" value={newSubjectType} onChange={(e) => setNewSubjectType(e.target.value)} placeholder={t('m37.performance.subjectTypePlaceholder')} /></label>
          <label>{t('m37.performance.subjectRef')}<input aria-label="field-subject-ref" value={subjectRef} onChange={(e) => setSubjectRef(e.target.value)} /></label>
          <label>{t('m37.common.value')}<input aria-label="field-value" type="number" value={metricValue} onChange={(e) => setMetricValue(e.target.value)} /></label>
          <label>{t('m37.common.unit')}<input aria-label="field-unit" value={metricUnit} onChange={(e) => setMetricUnit(e.target.value)} placeholder={t('m37.performance.unitPlaceholder')} /></label>
          <label>{t('m37.performance.benchmark')}<input aria-label="field-benchmark" type="number" value={benchmarkValue} onChange={(e) => setBenchmarkValue(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-performance" disabled={busy}>{busy ? t('m37.common.recording') : t('m37.performance.recordBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
        {notice && <p role="status" aria-label="notice">{notice}</p>}
      </section>

      <section aria-label="performance-list">
        <h2>{t('m37.performance.listTitle')}</h2>
        <div className="filters">
          <label>{t('m37.performance.metricType')}
            <select aria-label="filter-type" value={metricType} onChange={(e) => setMetricType(e.target.value)}>
              {['', ...METRIC_TYPES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.performance.subjectType')}<input aria-label="filter-subject-type" value={subjectType} onChange={(e) => setSubjectType(e.target.value)} placeholder={t('m37.performance.subjectFilterPlaceholder')} /></label>
          <label>{t('m37.performance.from')}<DateInput aria-label="filter-from" type="date" value={fromDate} onValueChange={(nextValue) => setFromDate(nextValue)} /></label>
          <label>{t('m37.performance.to')}<DateInput aria-label="filter-to" type="date" value={toDate} onValueChange={(nextValue) => setToDate(nextValue)} /></label>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="performance-count">{t('m37.performance.count', { count: total })}</p>
            <DataTable
              ariaLabel="performance-table"
              rows={rows}
              rowKey={(r) => r.performanceMetricId}
              columns={[
                { key: 'metricCode', header: t('m37.common.code'), render: (r) => r.metricCode },
                { key: 'metricType', header: t('m37.common.type'), render: (r) => r.metricType },
                { key: 'subject', header: t('m37.performance.colSubject'), render: (r) => <>{r.subjectType ?? '—'}{r.subjectRef ? ` · ${r.subjectRef}` : ''}</> },
                { key: 'metricValue', header: t('m37.common.value'), render: (r) => r.metricValue ?? '—' },
                { key: 'metricUnit', header: t('m37.common.unit'), render: (r) => r.metricUnit ?? '—' },
                { key: 'benchmarkValue', header: t('m37.performance.benchmark'), render: (r) => r.benchmarkValue ?? '—' },
                { key: 'variance', header: t('m37.performance.variance'), render: (r) => decimal(r.variance) },
                { key: 'computedAt', header: t('m37.performance.computed'), render: (r) => at(r.computedAt) },
              ]}
            />
          </>
        )}
      </section>
    </main>
  );
}
